import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/endpoints'
import { Card, Empty, ErrorState, Loading } from '../components/StateViews'
import { formatNumber, formatSigned } from '../format'

export function Companies() {
  const [search, setSearch] = useState('')
  const searchId = useId()

  const companies = useQuery({
    queryKey: ['companies', search],
    queryFn: ({ signal }) => api.companies(search || undefined, signal),
    // The list is retyped at every keystroke: keeping the previous page on
    // screen avoids a skeleton flashing under the cursor.
    placeholderData: keepPreviousData,
  })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Compagnie</h1>
        <p className="text-sm text-slate-500">
          Rete distributiva attuale e saldo degli ultimi 30 giorni.
        </p>
      </div>

      <Card>
        <div className="p-4">
          <label htmlFor={searchId} className="sr-only">
            Cerca una compagnia per ragione sociale
          </label>
          <input
            id={searchId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="cerca per ragione sociale"
            className="w-full max-w-md rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        {companies.isPending ? (
          <Loading />
        ) : companies.isError ? (
          <ErrorState error={companies.error} onRetry={() => void companies.refetch()} />
        ) : companies.data.data.length === 0 ? (
          <Empty
            title="Nessuna compagnia trovata."
            hint="Le compagnie compaiono con il primo snapshot applicato."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Compagnia
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Codice
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    Mandati attivi
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    Saldo 30 gg
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {companies.data.data.map((company) => (
                  <tr key={company.code} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link
                        to={`/companies/${company.code}`}
                        className="underline decoration-slate-300"
                      >
                        {company.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-slate-500">{company.code}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {formatNumber(company.active_mandates)}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${
                        company.change_30d > 0
                          ? 'text-emerald-700'
                          : company.change_30d < 0
                            ? 'text-rose-700'
                            : ''
                      }`}
                    >
                      {formatSigned(company.change_30d)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
